import { useEffect, useMemo, useRef, useState } from 'react';
import type { EstadoJogo } from '@core/motor/estados';
import { corridaEmCurso, nivelAtual, nivelDesdeDe } from '@core/motor/estados';
import {
  NIVEIS_POR_FASE,
  desafioDoNivel,
  faseDoNivel,
  nivelNaFase,
  progressoDoNivel,
  rotularObjetivo,
} from '@core/modos/palavredo';
import { MSG_ACHOU, MSG_CORRIDA_PARADA } from '@core/motor/mensagens';
import { formatarTempo, palavrasEscritas, restanteMs, rotularDuracao } from '@core/modos/corre';
import { JOGADAS_TRAMA, jogadasDe, jogadasRestantes } from '@core/modos/trama';
import { tamanhoDoDia } from '@core/termo/dia';
import { descobriu } from '@core/termo/partida';
import { msgPalavraDoDia } from '@core/termo/mensagens';

/**
 * Painel do modo em curso (§22).
 *
 * O Bistrô não tem painel: lá não há placar nem fim, e um painel zerado seria
 * uma cobrança que o modo não faz.
 */
export function PainelModo({
  estado,
  aoEsgotarTempo,
}: {
  estado: EstadoJogo;
  aoEsgotarTempo: () => void;
}) {
  if (estado.modo === 'termo') return <PainelTermo estado={estado} />;
  if (estado.modo === 'corre') {
    return <PainelCorre estado={estado} aoEsgotar={aoEsgotarTempo} />;
  }
  if (estado.modo === 'trama') return <PainelTrama estado={estado} />;
  if (estado.modo === 'palavredo') return <PainelPalavredo estado={estado} />;
  return null;
}

/**
 * Painel do Palavredo (§22.7).
 *
 * O nível em curso e o que ele pede, com a contagem ao lado. É o painel mais
 * cheio dos quatro, e tem de ser: nos outros o jogador sabe o que fazer —
 * escrever depressa, gastar dez jogadas, achar a palavra. Aqui o objetivo muda
 * a cada nível, e um painel que só dissesse o número deixaria a pessoa jogando
 * sem saber o que se cobra dela.
 *
 * A contagem `2/3` não é enfeite do ✓: §13 proíbe estado que dependa só de cor
 * ou de ícone, e é ela que diz por extenso o que o símbolo desenha.
 *
 * O progresso é derivado da cadeia a cada render (§22.7), e o circuito custa
 * duas varreduras do tabuleiro. Daí o `useMemo`: sem ele, cada tecla digitada
 * recontaria os retângulos de uma cadeia que não mudou.
 */
function PainelPalavredo({ estado }: { estado: EstadoJogo }) {
  const nivel = nivelAtual(estado);
  const desde = nivelDesdeDe(estado);

  const progresso = useMemo(
    () => progressoDoNivel(estado.cadeia, desde, desafioDoNivel(estado.semente, nivel)),
    [estado.cadeia, estado.semente, nivel, desde],
  );

  const fase = faseDoNivel(nivel);
  const acabou = estado.fase === 'fim';

  /*
   * O painel pisca a cada nível vencido (§22.7).
   *
   * O contador existe para reiniciar a animação: sem trocar a chave do
   * elemento, o CSS não roda a segunda vez — a animação já terminou naquele
   * nó, e nada a rearma. Começa em zero e o atributo só aparece depois do
   * primeiro incremento, senão o painel piscaria ao abrir a página, anunciando
   * um nível que ninguém acabou de passar.
   */
  const [pulso, setPulso] = useState(0);
  const nivelVisto = useRef(nivel);
  useEffect(() => {
    if (nivelVisto.current === nivel) return;
    nivelVisto.current = nivel;
    setPulso((n) => n + 1);
  }, [nivel]);

  return (
    <aside
      className="trama"
      aria-label="Nível do Palavredo"
      key={pulso}
      data-subiu={pulso > 0 ? '' : undefined}
    >
      <p className="trama__pontos">
        {/* Classe própria, e não `trama__unidade`: em tela estreita aquela some
            para o placar caber numa linha, e ali some a unidade de uma
            contagem — `12 pontos` continua se lendo como 12. Aqui sumiria a
            palavra `Fase`, e um `1` solto ao lado de uma lista de tarefas não
            quer dizer nada. */}
        <span className="nivel__rotulo">Fase</span> <span className="trama__valor">{fase}</span>
      </p>

      {/* O nível se conta dentro da fase, e não desde o começo de tudo: é a
          posição que diz quanto falta para o tabuleiro fechar. */}
      <p className="trama__linha">
        {acabou ? 'Fase completa' : `Nível ${nivelNaFase(nivel)} de ${NIVEIS_POR_FASE}`}
      </p>

      <ul className="nivel">
        {progresso.map((p) => (
          <li
            key={`${p.objetivo.tipo}-${'parte' in p.objetivo ? p.objetivo.parte : ''}`}
            className="nivel__objetivo"
            data-cumprido={p.cumprido}
          >
            <span className="nivel__marca" aria-hidden="true">
              {p.cumprido ? '✓' : '○'}
            </span>
            <span className="nivel__texto">{rotularObjetivo(p.objetivo)}</span>
            <span className="nivel__contagem">
              {Math.min(p.feito, p.objetivo.alvo)}/{p.objetivo.alvo}
            </span>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/**
 * Painel da Trama (§23).
 *
 * Uma linha só: o tamanho da palavra do dia — que varia (§23.1), e não saber o
 * tamanho seria uma incógnita a mais, não um desafio a mais.
 *
 * Não há placar. Contar as palavras gastas fazia da dedução uma corrida: quem
 * escrevesse uma palavra a mais só para testar uma letra veria o número subir
 * como se tivesse errado, e experimentar é justamente o que o modo pede.
 */
function PainelTermo({ estado }: { estado: EstadoJogo }) {
  if (estado.dia === undefined) return null;

  const achou = descobriu(estado.cadeia, estado.resposta);

  return (
    <aside className="trama trama--sem-placar" aria-label="Palavra do dia">
      <p className="trama__linha">
        {achou ? MSG_ACHOU : msgPalavraDoDia(tamanhoDoDia(estado.dia))}
      </p>
    </aside>
  );
}

/** Quantas vezes por segundo o cronômetro se redesenha. */
const PULSO_MS = 200;

/**
 * Cronômetro do Nitro (§22.2).
 *
 * É aqui que mora o relógio, e não no núcleo: o ADR-0001 proíbe `src/core` de
 * conhecer o tempo. Este componente compara o prazo com `Date.now()`, redesenha
 * sozinho — sem re-renderizar o tabuleiro a cada pulso — e avisa o motor uma
 * vez, quando o prazo vence.
 *
 * A comparação é sempre contra o relógio de parede, nunca contra um saldo que
 * se decrementa. É o que faz a aba em segundo plano, onde o navegador estrangula
 * os temporizadores, acertar o tempo assim que volta (T12-05).
 */
function PainelCorre({ estado, aoEsgotar }: { estado: EstadoJogo; aoEsgotar: () => void }) {
  const correndo = corridaEmCurso(estado);
  const prazo = estado.terminaEm;
  const [restante, setRestante] = useState(() =>
    prazo === undefined ? undefined : restanteMs(prazo, Date.now()),
  );

  // Um ref para o aviso: `aoEsgotar` muda de identidade a cada render do App, e
  // reiniciar o intervalo por causa disso perderia a contagem.
  const avisar = useRef(aoEsgotar);
  avisar.current = aoEsgotar;

  useEffect(() => {
    if (!correndo || prazo === undefined) return;

    const conferir = () => {
      const falta = restanteMs(prazo, Date.now());
      setRestante(falta);
      if (falta <= 0) avisar.current();
    };
    conferir();

    const id = window.setInterval(conferir, PULSO_MS);
    return () => window.clearInterval(id);
  }, [correndo, prazo]);

  const palavras = palavrasEscritas(estado.cadeia);
  const acabou = estado.fase === 'fim';
  const apertado = restante !== undefined && restante <= 10_000;

  return (
    <aside className="trama" aria-label="Cronômetro do Nitro">
      <p className="trama__pontos">
        <span className="trama__valor" data-apertado={!acabou && apertado}>
          {acabou
            ? formatarTempo(0)
            : correndo && restante !== undefined
              ? formatarTempo(restante)
              : rotularDuracao(estado.duracaoSegundos ?? 0)}
        </span>
      </p>

      <p className="trama__linha">
        {palavras} {palavras === 1 ? 'palavra' : 'palavras'}
      </p>

      {/* T12-02: enquanto não se digita, o cronômetro está parado — e dizer
          isso evita que a pessoa ache que já está perdendo tempo. */}
      {!acabou && !correndo && <p className="trama__linha">{MSG_CORRIDA_PARADA}</p>}
    </aside>
  );
}

/**
 * Painel do Átrio (§22.4).
 *
 * Três números, e só: o placar, em que jogada a partida está, e o
 * multiplicador em vigor. O último é o mais importante — é a aposta que já foi
 * feita e que a palavra em curso vai cobrar ou perder, e sem ele na tela a
 * escolha da âncora voltaria a parecer gratuita.
 *
 */
function PainelTrama({ estado }: { estado: EstadoJogo }) {
  const jogadas = jogadasDe(estado.cadeia);
  const restantes = jogadasRestantes(estado.cadeia);
  const emCurso = Math.min(jogadas + 1, JOGADAS_TRAMA);
  const acabou = estado.fase === 'fim';

  return (
    <aside className="trama" aria-label="Placar do Átrio">
      <p className="trama__pontos">
        <span className="trama__valor">{estado.pontos}</span>{' '}
        <span className="trama__unidade">{estado.pontos === 1 ? 'ponto' : 'pontos'}</span>
      </p>

      <p className="trama__linha">
        {acabou ? 'Partida encerrada' : `Jogada ${emCurso} de ${JOGADAS_TRAMA}`}
      </p>

      {/* A aposta só faz sentido enquanto há palavra para cobrá-la. */}
      {!acabou && estado.multiplicador > 1 && (
        <p className="trama__aposta">
          <span aria-hidden="true">×{estado.multiplicador}</span>
          <span className="apenas-leitor">Multiplicador {estado.multiplicador} nesta palavra</span>
        </p>
      )}

      {!acabou && restantes <= 3 && (
        <p className="trama__linha trama__linha--fim">
          {restantes === 1 ? 'última jogada' : `faltam ${restantes}`}
        </p>
      )}
    </aside>
  );
}
